import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    const viewMoreButton = this.querySelector('[data-search-view-more-button]');
    viewMoreButton?.addEventListener('click', this.handleSearchViewMore);

    this.#updateSearchViewMoreButton();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.querySelector('[data-search-view-more-button]')?.removeEventListener('click', this.handleSearchViewMore);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Loads the next page of search results.
   */
  handleSearchViewMore = async (event) => {
    event?.preventDefault();

    const button = this.querySelector('[data-search-view-more-button]');

    if (!(button instanceof HTMLButtonElement) || button.disabled || !this.hasNextPage()) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    await this.loadNextPage();

    button.disabled = false;
    button.removeAttribute('aria-busy');
    this.#updateSearchViewMoreButton();
  };

  #updateSearchViewMoreButton() {
    const container = this.querySelector('.search-results-view-more');
    const button = this.querySelector('[data-search-view-more-button]');
    const hasNext = this.hasNextPage();

    if (container instanceof HTMLElement) {
      container.hidden = !hasNext;
    }

    if (button instanceof HTMLButtonElement) {
      button.hidden = !hasNext;
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
